import axios, { AxiosResponse } from "axios";
import jwtDecode from "jwt-decode";
import { DecodedToken } from "../components/types/DecodedToken";
import { User } from "../components/types/User";
import { capture, identifyUser } from "../analytics/posthog";
import { AppNotification } from '../components/notifications/types';

const BASE_URL = process.env.REACT_APP_BOOKIE_BRAWLER_API_BASE_URL;
const accessTokenKey = "access_token";
const refreshTokenKey = "refresh_token";
const profilePicKey = "profile_pic";
const userRoleKey = "user_role";
const userEmailKey = "user_email";



export const getAccessToken = (): string | null =>
  localStorage.getItem(accessTokenKey);
export const setAccessTokenLocal = (token: string): void => {
  localStorage.setItem(accessTokenKey, token);
};
const getRefreshToken = (): string | null =>
  localStorage.getItem(refreshTokenKey);
export const setRefreshTokenLocal = (token: string): void =>
  localStorage.setItem(refreshTokenKey, token);

export const getProfilePic = (): string | null =>
  localStorage.getItem(profilePicKey);
export const setProfilePic = (url: string): void =>
  localStorage.setItem(profilePicKey, url);

export const getUserRole = (): string | null =>
  localStorage.getItem(userRoleKey);
export const setUserRole = (role: string): void =>
  localStorage.setItem(userRoleKey, role);

export const getUserEmail = (): string | null =>
  localStorage.getItem(userEmailKey);
export const setUserEmail = (email: string): void =>
  localStorage.setItem(userEmailKey, email);

export const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});
let requestInterceptor: number | null = null;

export const setAuthorizationHeader = (newAccessToken: string | null): void => {
  // Save the new access token in the local storage
  // setAccessToken(newAccessToken);

  // If there is an old interceptor, eject it
  if (requestInterceptor !== null) {
    apiClient.interceptors.request.eject(requestInterceptor);
  }

  // Set new interceptor with the new access token
  setInterceptorWithToken(newAccessToken);
};

const removeJsonContentTypeForFormData = (config: any) => {
  if (typeof FormData !== "undefined" && config.data instanceof FormData && config.headers) {
    if (typeof config.headers.delete === "function") {
      config.headers.delete("Content-Type");
      config.headers.delete("content-type");
    } else {
      delete config.headers["Content-Type"];
      delete config.headers["content-type"];
    }
  }
  return config;
};

export const setInterceptorWithToken = (accessToken: string | null) => {
  apiClient.interceptors.request.use(
    (config) => {
      if (accessToken) {
        config.headers["Authorization"] = `Bearer ${accessToken}`;
      }
      return removeJsonContentTypeForFormData(config);
    },
    (error: any) => {
      return Promise.reject(error);
    }
  );
};

//setInterceptorWithToken(getAccessToken());

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(accessTokenKey);
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return removeJsonContentTypeForFormData(config);
  },
  (error: any) => {
    return Promise.reject(error);
  }
);

export const setResponseInterceptorWithToken = (
  accessToken: string | null
): void => {
  apiClient.interceptors.response.use(
    (response: AxiosResponse) => {
      return response;
    },
    async (error: { config: any; response: { status: number } }) => {
      const originalRequest = error.config;
      if (
        error.response &&
        error.response.status === 401 &&
        !originalRequest._retry
      ) {
        originalRequest._retry = true;
        const isRefreshed = await refreshAccessToken();
        if (isRefreshed) {
          originalRequest.headers["Authorization"] = `Bearer ${accessToken}`;
          return apiClient(originalRequest);
        }
      }
      return Promise.reject(error);
    }
  );
};

//recreate in HandleTokens

//setResponseInterceptorWithToken(getAccessToken());

apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: { config: any; response: { status: number } }) => {
    const originalRequest = error.config;
    
    // Only attempt refresh on 401 errors, and only once per request
    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;
      
      const isRefreshed = await refreshAccessToken();
      
      if (isRefreshed) {
        // Retry with new token
        originalRequest.headers["Authorization"] =
          "Bearer " + localStorage.getItem(accessTokenKey);
        return apiClient(originalRequest);
      } else {
        // Refresh failed - tokens are cleared
        // Retry the request WITHOUT auth header for public endpoints
        delete originalRequest.headers["Authorization"];
        originalRequest._retryWithoutAuth = true;
        
        try {
          return await apiClient(originalRequest);
        } catch (retryError) {
          // If it still fails, it's not a public endpoint or there's another issue
          return Promise.reject(retryError);
        }
      }
    }
    
    // For 403 errors on refresh endpoint, clear tokens
    if (
      error.response &&
      error.response.status === 403 &&
      originalRequest.url?.includes('/refresh-token')
    ) {
      clearAuthTokens();
    }
    
    return Promise.reject(error);
  }
);

/**
 * Clears all auth tokens from localStorage.
 * Call this when refresh fails or user logs out.
 */
export const clearAuthTokens = (): void => {
  localStorage.removeItem(accessTokenKey);
  localStorage.removeItem(refreshTokenKey);
  localStorage.removeItem(profilePicKey);
  localStorage.removeItem(userRoleKey);
  localStorage.removeItem(userEmailKey);
  localStorage.removeItem("bb_user_brackets");
};

type RefreshTokenResult = {
  ok: boolean;
  status?: number;
  data?: any;
};

export const apiCallToRefreshToken = async (refreshToken: string): Promise<RefreshTokenResult> => {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${refreshToken}`,
  };

  try {
    const response = await fetch(BASE_URL + "/api/v1/auth/refresh-token", {
      method: "POST",
      headers,
    });

    if (response.ok) {
      return { ok: true, status: response.status, data: await response.json() };
    }
    return { ok: false, status: response.status };
  } catch (error) {
    console.error('Token refresh network error:', error);
    return { ok: false };
  }
};

let refreshPromise: Promise<boolean> | null = null;

export const refreshAccessToken = async (): Promise<boolean> => {
  // If already refreshing, wait for that attempt
  if (refreshPromise) {
    return refreshPromise;
  }

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    clearAuthTokens();
    return false;
  }

  refreshPromise = (async () => {
    try {
      const result = await apiCallToRefreshToken(refreshToken);
      const tokenData = result.ok ? result.data : null;

      if (tokenData && tokenData.access_token) {
        setAccessTokenLocal(tokenData.access_token);
        const nextRefreshToken = tokenData.refresh_token || tokenData.refresh_tokens;
        if (nextRefreshToken) {
          setRefreshTokenLocal(nextRefreshToken);
        }
        return true;
      }

      if (result.status === 401 || result.status === 403) {
        console.log('Token refresh was rejected - clearing auth tokens');
        clearAuthTokens();
      } else {
        console.warn('Token refresh failed without auth rejection; preserving current session');
      }
      return false;
    } catch (error) {
      console.error('Token refresh error:', error);
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

export const __resetAuthRefreshStateForTests = (): void => {
  refreshPromise = null;
};
//recreate in HandleTokens for full context usage

export const getRequest = async (url: string, useCache: boolean = true): Promise<any> => {
  try {
    const response = await apiClient.get(url, {
      headers: useCache ? {} : { 'Cache-Control': 'public, max-age=15, must-revalidate' }
    });
    return response;
  } catch (error) {
    console.error("Failed to fetch data", error);
    return null;
  }
};

export const postRequest = async (
  url: string,
  body: any,
  isMultipart = false
): Promise<any> => {
  try {
    const response = await apiClient.post(
      url,
      body,
      isMultipart
        ? {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        : {}
    );
    return response.data;
  } catch (error) {
    console.error("Failed to post data", error);
    return null;
  }
};

export const patchRequest = async (url: string, body: any): Promise<any> => {
  try {
    const response = await apiClient.patch(url, body);
    return response.data;
  } catch (error) {
    console.error("Failed to update data", error);
    return null;
  }
};

export const deleteRequest = async (url: string): Promise<any> => {
  try {
    const response = await apiClient.delete(url);
    return response.data;
  } catch (error) {
    console.error("Failed to delete data", error);
    return null;
  }
};

export const getCurrentUserPromise = async (): Promise<any> => {
  const url = "/api/v1/users/me";
  setAuthorizationHeader(await getAccessToken());
  const response = await getRequest(url);
  return response;
};

/**
 * Encodes a user ID into an obfuscated string for public profile URLs.
 * Prevents sequential scraping of user profiles.
 */
export const encodeProfileId = (id: number): string => {
  const payload = `bb_${id}_${id * 7919}`; // 7919 is a prime, adds pseudo-random suffix
  return btoa(payload).replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_');
};

/**
 * Generates a full profile URL path for a given user ID (encoded).
 * Usage: <Link to={getProfileUrl(user.id)}>View Profile</Link>
 */
export const getProfileUrl = (userId: number): string => {
  return `/profile/${encodeProfileId(userId)}`;
};

/**
 * Generates a full profile URL path using a handle.
 * Preferred when handle is available as it's more readable.
 * Usage: <Link to={getProfileUrlByHandle(user.handle)}>View Profile</Link>
 */
export const getProfileUrlByHandle = (handle: string): string => {
  return `/profile/${handle}`;
};

/**
 * Checks if a string looks like a default/auto-generated username.
 * Default format: email + UUID like "useremmanuel.mcmanus@gmail.comd3711230-ceb9-4a14-a56c-becc5c5326b4"
 */
const isDefaultUsername = (str: string): boolean => {
  if (!str) return true;
  // Check for UUID pattern (8-4-4-4-12 hex chars)
  const uuidPattern = /[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/i;
  // Check for email-like pattern
  const emailPattern = /@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;
  
  return uuidPattern.test(str) || (emailPattern.test(str) && str.length > 30);
};

/**
 * Generates profile URL - prefers handle if available, falls back to encoded ID.
 * Usage: <Link to={getSmartProfileUrl(user)}>View Profile</Link>
 */
export const getSmartProfileUrl = (user: { id: number; handle?: string }): string => {
  // Only use handle if it's not a default/auto-generated username
  if (user.handle && !isDefaultUsername(user.handle)) {
    return getProfileUrlByHandle(user.handle);
  }
  return getProfileUrl(user.id);
};

/**
 * Formats a display name for a user.
 * - If handle exists and is not a default format, returns @handle
 * - If firstname exists, returns "Firstname L." (with last initial)
 * - Falls back to 'Brawler' (avoids showing default/generated usernames)
 */
export const getDisplayName = (user: { handle?: string; firstname?: string; firstName?: string; lastname?: string; lastName?: string; username?: string }): string => {
  // Priority 1: Valid handle (not default UUID pattern)
  if (user.handle && !isDefaultUsername(user.handle)) {
    return `@${user.handle}`;
  }
  
  // Priority 2: Valid username (not default UUID pattern)
  if (user.username && !isDefaultUsername(user.username)) {
    return user.username;
  }
  
  // Priority 3: Firstname + last initial (fallback when no valid handle/username)
  const firstName = user.firstname || user.firstName;
  const lastName = user.lastname || user.lastName;
  
  if (firstName) {
    const lastInitial = lastName ? ` ${lastName.charAt(0)}.` : '';
    return `${firstName}${lastInitial}`;
  }
  
  return 'Brawler';
};

/**
 * Formats a screen name with @ prefix for display as a profile link.
 * Use this for share headers where screenName comes from backend.
 * Returns null if screenName is a default/auto-generated format.
 */
export const formatScreenName = (screenName: string): string | null => {
  // Don't format default/auto-generated usernames
  if (isDefaultUsername(screenName)) return null;
  
  // If it already has @, return as-is
  if (screenName.startsWith('@')) return screenName;
  return `@${screenName}`;
};

/**
 * Formats a last active date into a human-readable relative time string.
 * Similar to social media "Active 2h ago", "Active 3d ago" style.
 * Falls back to "Active" if date is null/undefined.
 * 
 * @param dateString - ISO date string or Date object
 * @returns Formatted string like "Active now", "Active 2h ago", "Active 3d ago", etc.
 */
export const formatLastActive = (dateString?: string | Date | null): string => {
  
  if (!dateString) return 'Active';

  try {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
  
    // Handle future dates or invalid dates
    if (diffMs < 0 || isNaN(diffMs)) return 'Active';


    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    // Just now / seconds ago
    if (diffSecs < 60) return 'Active now';
    
    // Minutes ago
    if (diffMins < 60) {
      return diffMins === 1 ? 'Active 1m ago' : `Active ${diffMins}m ago`;
    }
    
    // Hours ago
    if (diffHours < 24) {
      return diffHours === 1 ? 'Active 1h ago' : `Active ${diffHours}h ago`;
    }
    
    // Days ago
    if (diffDays < 7) {
      return diffDays === 1 ? 'Active 1d ago' : `Active ${diffDays}d ago`;
    }
    
    // Weeks ago
    if (diffWeeks < 4) {
      return diffWeeks === 1 ? 'Active 1w ago' : `Active ${diffWeeks}w ago`;
    }
    
    // Months ago
    if (diffMonths < 12) {
      return diffMonths === 1 ? 'Active 1mo ago' : `Active ${diffMonths}mo ago`;
    }
    
    // Years ago
    return diffYears === 1 ? 'Active 1y ago' : `Active ${diffYears}y ago`;
  } catch {
    return 'Active';
  }
};

/**
 * Decodes an obfuscated profile string back to a user ID.
 * Returns null if invalid.
 */
export const decodeProfileId = (encoded: string): number | null => {
  try {
    // Restore base64 padding and chars
    let base64 = encoded.replace(/-/g, '+').replace(/_/g, '/');
    while (base64.length % 4) base64 += '=';
    const decoded = atob(base64);
    const match = decoded.match(/^bb_(\d+)_(\d+)$/);
    if (!match) return null;
    const id = parseInt(match[1]);
    const checksum = parseInt(match[2]);
    // Validate checksum
    if (checksum !== id * 7919) return null;
    return id;
  } catch {
    return null;
  }
};

/**
 * Fetches a user's public profile card.
 * @param profileId - Either an encoded user ID or a handle (e.g., "bloohunnits")
 * Backend accepts both formats.
 */
export const getUserCard = async (profileId: string): Promise<User | null> => {
  const url = `/api/v1/users/card/${profileId}`;
  const response = await getRequest(url);
  return response?.data || null;
};

// ─── Social / Follow API ───

export interface FollowStats {
  followers_count: number;
  following_count: number;
  is_following: boolean;
}

export interface FollowUser {
  id: number;
  handle?: string;
  firstname?: string;
  lastname?: string;
  username?: string;
  picture?: string;
  affiliated?: boolean;
}

/**
 * Get follower/following counts + whether the current user follows this user.
 * Throws when the request fails or returns no data so callers can distinguish
 * "no data / not authenticated" from a real `is_following: false` answer.
 */
export const getFollowStats = async (userId: number): Promise<FollowStats> => {
  const response = await getRequest(`/api/v1/social/${userId}/stats`);
  if (!response?.data) {
    throw new Error('getFollowStats: empty response');
  }
  return response.data;
};

/** Follow a user */
export const followUser = async (userId: number): Promise<boolean> => {
  try {
    const result = await postRequest(`/api/v1/social/${userId}/follow`, {});
    console.log('[Follow] followUser result:', result);
    return !!result;
  } catch (error) {
    console.error('[Follow] followUser error:', error);
    return false;
  }
};

/** Unfollow a user */
export const unfollowUser = async (userId: number): Promise<boolean> => {
  try {
    const result = await deleteRequest(`/api/v1/social/${userId}/follow`);
    console.log('[Follow] unfollowUser result:', result);
    return true;
  } catch (error) {
    console.error('[Follow] unfollowUser error:', error);
    return false;
  }
};

/** Get followers list for a user */
export const getFollowers = async (userId: number): Promise<FollowUser[]> => {
  try {
    const response = await getRequest(`/api/v1/social/${userId}/followers`);
    console.log('[Follow] getFollowers response:', response?.data);
    return response?.data ?? [];
  } catch (error) {
    console.error('[Follow] getFollowers error:', error);
    return [];
  }
};

/** Get following list for a user */
export const getFollowing = async (userId: number): Promise<FollowUser[]> => {
  try {
    const response = await getRequest(`/api/v1/social/${userId}/following`);
    console.log('[Follow] getFollowing response:', response?.data);
    return response?.data ?? [];
  } catch (error) {
    console.error('[Follow] getFollowing error:', error);
    return [];
  }
};



// ─── Notifications API ───

/**
 * Fetch the current user's notifications, newest first. With no arguments
 * the full list is returned (existing behavior); pass page+size for a slice
 * (e.g. an infinite-scroll inbox).
 */
export const getNotifications = async (page?: number, size?: number): Promise<AppNotification[]> => {
  try {
    const query = size != null ? `?page=${page ?? 0}&size=${size}` : '';
    const response = await getRequest(`/api/v1/notifications${query}`);
    return response?.data ?? [];
  } catch (error) {
    console.error('[Notifications] getNotifications error:', error);
    return [];
  }
};

/** Get count of unread notifications */
export const getUnreadNotificationCount = async (): Promise<number> => {
  try {
    const response = await getRequest('/api/v1/notifications/unread-count');
    return response?.data?.count ?? 0;
  } catch (error) {
    console.error('[Notifications] getUnreadCount error:', error);
    return 0;
  }
};

/** Mark a single notification as read */
export const markNotificationRead = async (notificationId: number): Promise<boolean> => {
  try {
    await putRequest(`/api/v1/notifications/${notificationId}/read`, {});
    return true;
  } catch (error) {
    console.error('[Notifications] markRead error:', error);
    return false;
  }
};

/** Mark all notifications as read */
export const markAllNotificationsRead = async (): Promise<boolean> => {
  try {
    await putRequest('/api/v1/notifications/read-all', {});
    return true;
  } catch (error) {
    console.error('[Notifications] markAllRead error:', error);
    return false;
  }
};

// ─── Push endpoints API (web push devices) ───
// The token always travels in a POST body, never a URL, and the backend
// binds it to the authenticated user — no userId is ever sent.

export interface PushEndpointInfo {
  id: number;
  platform: string | null;
  browser: string | null;
  device_name: string | null;
  active: boolean;
  token_suffix: string | null;
  created_at: string | null;
  last_seen_at: string | null;
  last_success_at: string | null;
}

/** Register (or refresh) this browser's push token for the current user. */
export const registerPushEndpoint = async (payload: {
  token: string;
  platform?: string;
  browser?: string;
  device_name?: string;
}): Promise<PushEndpointInfo | null> => {
  try {
    return await postRequest('/api/v1/notifications/push-endpoints', payload);
  } catch (error) {
    console.error('[Push] registerPushEndpoint error:', error);
    return null;
  }
};

/** Deactivate this browser's push token (logout / notifications off). */
export const unregisterPushEndpoint = async (token: string): Promise<boolean> => {
  try {
    const response = await postRequest('/api/v1/notifications/push-endpoints/unregister', { token });
    return response?.success === true;
  } catch (error) {
    console.error('[Push] unregisterPushEndpoint error:', error);
    return false;
  }
};

/** List the current user's push devices (tokens never included). */
export const listPushEndpoints = async (): Promise<PushEndpointInfo[]> => {
  try {
    const response = await getRequest('/api/v1/notifications/push-endpoints', false);
    return response?.data ?? [];
  } catch (error) {
    console.error('[Push] listPushEndpoints error:', error);
    return [];
  }
};

/** Ask the backend to send a real test push to this user's devices. */
export const sendTestPushNotification = async (): Promise<boolean> => {
  try {
    const response = await postRequest('/api/v1/notifications/push-endpoints/test', {});
    return response?.success === true;
  } catch (error) {
    console.error('[Push] sendTestPushNotification error:', error);
    return false;
  }
};

export const putRequest = async (url: string, body: any): Promise<any> => {
  try {
    const response = await apiClient.put(url, body);
    return response;
  } catch (error) {
    console.error("Failed to update data", error);
    return null;
  }
};

export function isTokenExpired(decodedToken: DecodedToken): boolean {
  const currentTime = Date.now() / 1000;
  return decodedToken.exp < currentTime;
}

export const getIsAuthenticated = async (): Promise<boolean> => {
  const accessToken = getAccessToken();

  if (!accessToken) {
    return false;
  }

  try {
    const decodedToken = jwtDecode<DecodedToken>(accessToken);

    if (isTokenExpired(decodedToken)) {
      const isRefreshed = await refreshAccessToken();
      if (!isRefreshed) {
        console.error("Token expired must re-authenticate");
        return false;
      }
    }

    return true;
  } catch (error) {
    console.error("Failed to decode access token:", error);
    return false;
  }
};


export const getCurrentUser = async (): Promise<any> => {
  const authed = await getIsAuthenticated();
  if (authed) return getCurrentUserPromise();
  return 
};

const apiCallToGoogleAuth = async (
  authToken: any
): Promise<{ user: User; tokens: any } | null> => {
  const authorizationToken = authToken;

  try {
    const response = await fetch(
      BASE_URL + "/api/v1/auth/authenticate/google",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ authorizationToken }),
      }
    );

    if (response.ok) {
      const authResponse = await response.json();
      console.log("Login successful");
      return { user: authResponse.user, tokens: authResponse };
    } else {
      console.error("Login failed");
      return null;
    }
  } catch (error) {
    console.error("Error:", error);
    return null;
  }
};

const apiCallToGoogleAuthFirebase = async (authToken: any) => {
  const authorizationToken = authToken;

  try {
    const response = await fetch(
      BASE_URL + "/api/v1/auth/authenticate/google/base",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ idToken: authorizationToken }),
      }
    );

    if (response.ok) {
      const authResponse = await response.json();
      return { user: authResponse.user, tokens: authResponse };
    } else {
      console.error("Login failed");
      return null;
    }
  } catch (error) {
    console.error("Error:", error);
    return null;
  }
};

export const handleGoogleSignInAuthTokens = async (
  authToken: any,
  useFirebase?: boolean
): Promise<{ user: User; tokens: any } | null> => {
  const response = useFirebase
    ? await apiCallToGoogleAuthFirebase(authToken)
    : await apiCallToGoogleAuth(authToken);

  if (response) {
    // Remove this when you no longer need to handle local storage for auth
    setAccessTokenLocal(response.tokens.access_token);
    setRefreshTokenLocal(response.tokens.refresh_token);
    setAuthorizationHeader(response.tokens.access_token);
    setProfilePic(response.user.picture);
    setUserRole(response.user.role);
    if (response.user.email) setUserEmail(response.user.email);
    identifyUser(response.user);
    const isNewAccount = response.tokens.new_account === true;
    capture(isNewAccount ? "signup_completed" : "login_completed", {
      method: "google", role: String(response.user.role), measurement_version: 2,
    });
    sessionStorage.setItem("bb_new_account", isNewAccount ? "true" : "false");
    return response;
  } else {
    return null;
  }
};

// export const handleGoogleAuthSuccess = async (
//   credentialResponse: any,

// ): Promise<{ user: User; tokens: any } | null> => {
//   const authorizationToken = credentialResponse.credential;

//   try {
//     const response = await fetch(
//       "http://localhost:8080/api/v1/auth/authenticate/google",
//       {
//         method: "POST",
//         headers: {
//           "Content-Type": "application/json",
//         },
//         body: JSON.stringify({ authorizationToken }),
//       }
//     );

//     if (response.ok) {
//       const authResponse = await response.json();

//       console.log("Login successful authRe", authResponse);
//       setAccessTokenLocal(authResponse.access_token);
//       setRefreshTokenLocal(authResponse.refresh_token);

//       return { user: authResponse.user, tokens: authResponse };
//     } else {
//       console.error("Login failed");
//       //setUser({} as User);  // unset the user in case of failure
//       return null;
//     }
//   } catch (error) {
//     console.error("Error:", error);
//     return null;
//   }
// };

export const logout = async (
  setIsUserAuthenticated?: React.Dispatch<React.SetStateAction<boolean>>
): Promise<void> => {
  try {
    const response = await apiClient.post("/api/v1/auth/logout");

    if (response.status === 200) {
      localStorage.removeItem(accessTokenKey);
      localStorage.removeItem(refreshTokenKey);
      localStorage.removeItem(profilePicKey);
      localStorage.removeItem(userRoleKey);
      localStorage.removeItem(userEmailKey);
      localStorage.removeItem("bb_user_brackets");
    //  setIsUserAuthenticated(false);
      console.log("You've been logged out successfully.");
    } else {
      console.error("Failed to log out on server side.");
    }
  } catch (error) {
    console.error("An error occurred during logout: ", error);
  }
};

// export async function getUserEmail(): Promise<string | null> {
//   const accessToken = getAccessToken();
//   if (!accessToken) {
//     return null;
//   }

//   const decodedToken = jwtDecode<DecodedToken>(accessToken);

//   if (isTokenExpired(decodedToken)) {
//     const isRefreshed = await refreshAccessToken();
//     if (!isRefreshed) {
//       return null;
//     }
//     const newAccessToken = getAccessToken();
//     if (!newAccessToken) {
//       return null;
//     }
//     const newDecodedToken = jwtDecode<DecodedToken>(newAccessToken);
//     return newDecodedToken.sub;
//   }

//   return decodedToken.sub;
// }
